"use client";

import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Loader2, Search, SearchX, X } from "lucide-react";
import { SiteNavbar } from "@/shared/ui/site-navbar";
import { SiteFooter } from "@/shared/ui/site-footer";
import type { AppLocale } from "@/core/i18n/config";
import { localizeHref } from "@/core/i18n/routing";
import { cn } from "@/shared/lib/utils";
import type { SiteFooterContent, SiteNavbarContent } from "@/shared/types/site-shell-content";
import type { SearchResultsResponse, SearchType } from "../../types";
import { useSearch } from "../hooks/use-search";
import {
  BlogResultCard,
  HealerResultCard,
  ResultCardSkeleton,
} from "../components/search-result-cards";

interface SearchPageProps {
  navigation: SiteNavbarContent;
  footer: SiteFooterContent;
  locale: AppLocale;
  initialQuery?: string;
  initialType?: SearchType;
  initialData?: SearchResultsResponse | null;
}

const TYPE_TABS: { value: SearchType; en: string; hi: string }[] = [
  { value: "all", en: "All", hi: "सभी" },
  { value: "healers", en: "Healers", hi: "हीलर" },
  { value: "blogs", en: "Articles", hi: "लेख" },
];

const POPULAR_EN = ["Anxiety", "Trauma", "Couples therapy", "Burnout", "Mindfulness"];
const POPULAR_HI = ["चिंता", "आघात", "युगल थेरेपी", "बर्नआउट", "माइंडफुलनेस"];

const listMotion = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.06 } },
};

export function SearchPage({
  navigation,
  footer,
  locale,
  initialQuery = "",
  initialType = "all",
  initialData = null,
}: SearchPageProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const isHindi = locale === "hi";

  const { query, setQuery, type, setType, data, loading, error, hasSearched, clear } = useSearch({
    initialQuery,
    initialType,
    initialData,
  });

  // Keep the URL query string in sync so results are shareable / refreshable.
  useEffect(() => {
    const trimmed = query.trim();
    const params = new URLSearchParams();
    if (trimmed) params.set("q", trimmed);
    if (type !== "all") params.set("type", type);
    const qs = params.toString();
    const url = localizeHref(locale, `/search${qs ? `?${qs}` : ""}`);
    window.history.replaceState(null, "", url);
  }, [query, type, locale]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const healers = data?.results.healers?.items ?? [];
  const blogs = data?.results.blogs?.items ?? [];
  const totalHealers = data?.results.healers?.total ?? 0;
  const totalBlogs = data?.results.blogs?.total ?? 0;
  const totalResults = totalHealers + totalBlogs;
  const showHealers = type === "all" || type === "healers";
  const showBlogs = type === "all" || type === "blogs" || type === "content";

  const suggestedQuery = data?.suggestedQuery ?? data?.correctedQuery ?? null;

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-background font-sans text-foreground selection:bg-primary/20">
      <SiteNavbar content={navigation} />

      {/* Search hero */}
      <section className="relative overflow-hidden border-b border-border/60 bg-[#122023] px-6 pt-32 pb-12 text-white sm:pt-36">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(21,90,125,0.5),transparent_32%)]" />
        <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-primary/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-16 h-72 w-72 rounded-full bg-secondary/10 blur-3xl" />

        <div className="relative mx-auto w-full max-w-3xl text-center">
          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="font-heading text-3xl font-light tracking-tight sm:text-4xl"
          >
            {isHindi ? (
              <>
                <span className="font-semibold">हीलर</span> और लेख खोजें
              </>
            ) : (
              <>
                Find <span className="font-semibold">healers</span> & articles
              </>
            )}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="mx-auto mt-3 max-w-xl text-sm font-light text-white/70"
          >
            {isHindi
              ? "विशेषज्ञता, स्थान या विषय के अनुसार सत्यापित हीलर और मार्गदर्शक लेख खोजें।"
              : "Search verified healers and guided articles by specialty, location, or topic."}
          </motion.p>

          {/* Search inbox */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="relative mx-auto mt-8 max-w-2xl"
          >
            <div className="group flex items-center gap-3 rounded-full border border-white/15 bg-card/95 px-5 py-3.5 shadow-[0_18px_50px_rgba(0,0,0,0.25)] backdrop-blur transition focus-within:border-secondary focus-within:shadow-[0_18px_60px_rgba(125,226,248,0.25)]">
              <Search className="h-5 w-5 shrink-0 text-muted-foreground" />
              <input
                ref={inputRef}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                type="search"
                inputMode="search"
                placeholder={
                  isHindi
                    ? "जैसे, 'चिंता', 'मुंबई में थेरेपिस्ट'..."
                    : "Try 'anxiety', 'therapist in Mumbai'..."
                }
                className="w-full bg-transparent text-base text-foreground placeholder:text-muted-foreground focus:outline-none"
                aria-label={isHindi ? "खोजें" : "Search"}
              />
              {loading && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" />}
              {query && !loading && (
                <button
                  type="button"
                  onClick={() => {
                    clear();
                    inputRef.current?.focus();
                  }}
                  aria-label={isHindi ? "साफ़ करें" : "Clear"}
                  className="shrink-0 rounded-full p-1 text-muted-foreground transition hover:bg-muted hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Popular searches */}
            {!hasSearched && !query && (
              <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                <span className="text-xs text-white/50">{isHindi ? "लोकप्रिय:" : "Popular:"}</span>
                {(isHindi ? POPULAR_HI : POPULAR_EN).map((term) => (
                  <button
                    key={term}
                    type="button"
                    onClick={() => setQuery(term)}
                    className="rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-medium text-white/80 transition hover:border-white/40 hover:bg-white/20"
                  >
                    {term}
                  </button>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Results */}
      <section className="mx-auto w-full max-w-7xl px-4 py-10 sm:px-6 sm:py-12">
        {/* Tabs + summary */}
        {(hasSearched || loading) && (
          <div className="mb-8 flex flex-col gap-4 border-b border-border/60 pb-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="inline-flex rounded-full border border-border bg-card p-1">
              {TYPE_TABS.map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => setType(tab.value)}
                  className={cn(
                    "rounded-full px-4 py-1.5 text-sm font-medium transition-colors",
                    type === tab.value || (type === "content" && tab.value === "blogs")
                      ? "bg-primary text-white shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {isHindi ? tab.hi : tab.en}
                </button>
              ))}
            </div>
            {hasSearched && !loading && !error && (
              <p className="text-sm font-light text-muted-foreground">
                {totalResults > 0 ? (
                  <>
                    <span className="font-semibold text-foreground">{totalResults}</span>{" "}
                    {isHindi ? "परिणाम के लिए" : "results for"}{" "}
                    <span className="font-medium text-foreground/80">“{data?.query}”</span>
                  </>
                ) : (
                  <>{isHindi ? "कोई परिणाम नहीं" : "No results"}</>
                )}
              </p>
            )}
          </div>
        )}

        {/* Did you mean */}
        {hasSearched && !loading && suggestedQuery && suggestedQuery !== data?.query && (
          <p className="mb-6 text-sm text-muted-foreground">
            {isHindi ? "क्या आपका मतलब था" : "Did you mean"}{" "}
            <button
              type="button"
              onClick={() => setQuery(suggestedQuery)}
              className="font-semibold text-primary underline-offset-2 hover:underline"
            >
              {suggestedQuery}
            </button>
            ?
          </p>
        )}

        {/* Loading skeletons */}
        {loading && (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <ResultCardSkeleton key={index} />
            ))}
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="rounded-2xl border border-dashed border-rose-200 bg-rose-50/50 py-16 text-center">
            <SearchX className="mx-auto mb-3 h-9 w-9 text-rose-400" />
            <p className="font-light text-muted-foreground">
              {isHindi
                ? "खोज पूरी नहीं हो सकी। कृपया पुनः प्रयास करें।"
                : "Something went wrong. Please try again."}
            </p>
          </div>
        )}

        {/* Results */}
        {!loading && !error && hasSearched && (
          <div className="space-y-12">
            {showHealers && healers.length > 0 && (
              <div>
                {type === "all" && (
                  <h2 className="mb-5 font-heading text-xl font-light text-foreground">
                    {isHindi ? "हीलर" : "Healers"}{" "}
                    <span className="text-sm font-normal text-muted-foreground">({totalHealers})</span>
                  </h2>
                )}
                <motion.div
                  variants={listMotion}
                  initial="hidden"
                  animate="show"
                  className="grid grid-cols-1 gap-6 sm:grid-cols-2"
                >
                  {healers.map((healer) => (
                    <HealerResultCard key={healer.id} healer={healer} locale={locale} />
                  ))}
                </motion.div>
              </div>
            )}

            {showBlogs && blogs.length > 0 && (
              <div>
                {type === "all" && (
                  <h2 className="mb-5 font-heading text-xl font-light text-foreground">
                    {isHindi ? "लेख" : "Articles"}{" "}
                    <span className="text-sm font-normal text-muted-foreground">({totalBlogs})</span>
                  </h2>
                )}
                <motion.div
                  variants={listMotion}
                  initial="hidden"
                  animate="show"
                  className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
                >
                  {blogs.map((blog) => (
                    <BlogResultCard key={blog.id} blog={blog} locale={locale} />
                  ))}
                </motion.div>
              </div>
            )}

            {/* Empty results */}
            {totalResults === 0 && (
              <div className="rounded-2xl border border-dashed border-border bg-card/40 py-20 text-center">
                <SearchX className="mx-auto mb-4 h-10 w-10 text-muted-foreground" />
                <p className="font-heading text-lg font-light text-foreground/80">
                  {isHindi
                    ? `“${data?.query}” के लिए कुछ नहीं मिला`
                    : `No results for “${data?.query}”`}
                </p>
                <p className="mx-auto mt-2 max-w-md text-sm font-light text-muted-foreground">
                  {isHindi
                    ? "वर्तनी जाँचें या कोई और कीवर्ड आज़माएँ।"
                    : "Check your spelling or try a different keyword."}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Idle prompt */}
        {!loading && !hasSearched && !error && (
          <div className="py-20 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
              <Search className="h-6 w-6 text-primary" />
            </div>
            <p className="font-heading text-lg font-light text-muted-foreground">
              {isHindi ? "शुरू करने के लिए ऊपर टाइप करें" : "Start typing to search"}
            </p>
          </div>
        )}
      </section>

      <SiteFooter content={footer} />
    </div>
  );
}
